import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { catchError, finalize, first, of, Subject, takeUntil, tap } from 'rxjs';

import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { DataListCnpjs, ListCnpjsResponseModel } from '../../core/models/responses/list-cnpjs-response.model';
import { CnpjService } from '../../core/services/common/cnpj.service';
import { FormatCnpjPipe } from '../../core/pipes/format-cnpj.pipe';

@Component({
    selector: 'app-combobox-cnpj',
    imports: [CommonModule, FormsModule, NgxMaskDirective, FormatCnpjPipe],
    providers: [
        provideNgxMask()
    ],
    templateUrl: './combobox-cnpj.component.html',
    styleUrl: './combobox-cnpj.component.scss'
})
export class ComboboxCnpjComponent implements OnInit {
  private destroy$: Subject<void> = new Subject<void>();

  @Input() isGlobalCnpj: boolean = false;
  @Input() isBackgroundWhite: boolean = false;

  @Output() onCnpjSelected: EventEmitter<string> = new EventEmitter<string>();

  private cnpjsResponse: ListCnpjsResponseModel = {} as ListCnpjsResponseModel;
  public filteredCnpjs: WritableSignal<DataListCnpjs[]> = signal<DataListCnpjs[]>([]);

  public isVisibleListCnpjs: WritableSignal<boolean> = signal<boolean>(false);
  public isLoading: WritableSignal<boolean> = signal<boolean>(false);
  public submitStatus = {
    success: false,
    error: false
  }

  public selectedCnpj: string = '';

  constructor(
    private cnpjService: CnpjService
  ) { }

  ngOnInit(): void {
    this.resetStatusSubmit();
    this.getCnpjs();

    if (this.isGlobalCnpj) {
      this.initializeSelectedCnpj();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSelectedCnpj(): void {
    this.selectedCnpj = this.cnpjService.getSelectedCnpj();

    this.cnpjService.selectedCnpj$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cnpj) => {
        this.selectedCnpj = cnpj;
      });
  }

  private getCnpjs(): void {
    this.isLoading.set(true);

    this.cnpjService.getCnpjs()
      .pipe(
        first(response => response && response.data && response.data.length > 0),
        takeUntil(this.destroy$),
        tap((response) => {
          this.cnpjsResponse = response;
          this.filteredCnpjs.set(this.cnpjsResponse.data);
          // TODO: Validar se existe na lista de CNPJ antes de setar
          this.handleSuccess();
        }),
        catchError((error) => {
          console.error('Erro ao buscar CNPJs', error);
          this.handleError();
          return of([]);
        }),
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe({
        next(value) {
        },
        error(error) {
          console.error('Error fetching CNPJs:', error);
        }
      });
  }

  private resetStatusSubmit(): void {
    this.submitStatus.error = false;
    this.submitStatus.success = false;
  }

  private handleSuccess(): void {
    this.submitStatus.success = true;
  }

  private handleError(): void {
    this.submitStatus.error = true;
  }

  public onSelectCnpj(cnpj: string): void {
    this.selectedCnpj = cnpj;

    if (this.isGlobalCnpj) {
      this.cnpjService.setSelectedCnpj(cnpj);
      this.toggleInputCnpj();
      this.cnpjService.showCnpjPopup(false);
    } else {
      this.onCnpjSelected.emit(this.selectedCnpj);
      this.toggleInputCnpj();
    }
  }

  public onFilterCnpj(): void {
    this.filteredCnpjs.set(this.cnpjsResponse.data.filter((data) =>
      data.cnpj.startsWith(this.selectedCnpj)
    ));
  }

  public toggleInputCnpj(): void {
    this.isVisibleListCnpjs.update(value => value = !value);
  }
}
